import { Button, type DialogRef, IconButton, Input, OdinIconSize, Tooltip } from '@myosh/odin-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Subject, take, takeUntil } from 'rxjs';
import type { DynamicFormSettings } from '../../@types/forms';
import useDynamicFormNotifier from '../../hooks/use-dynamic-form-notifier';
import useProfileData from '../../hooks/use-profile-data';
import { useUserAccess } from '../../hooks/use-user-access';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  useUpdateBulkRecordAiAnalysisPromptMutation,
  useUpdateSingleRecordAiAnalysisPromptMutation,
} from '../../redux/services/ai-api';
import { usePostFormByReferenceIdMutation, usePutFormByIdMutation } from '../../redux/services/api';
import { removeBackgroundJob } from '../../redux/slices/background-job-slice';
import {
  resetAiSpecificConfig,
  resetState,
  selectBulkRecordAIAnalysisPrompt,
  selectFormCaption,
  selectFormData,
  selectHasDeletedItems,
  selectIsDirty,
  selectSingleRecordAIAnalysisPrompt,
  setFormLoadingState,
  updateFormCaption,
} from '../../redux/slices/form-builder-slice';
import { showError, showSuccess } from '../../services/notification.service';
import { extractErrorMessage } from '../../utils/error-utils';
import { FormsPermissions } from '../../utils/user.permissions';
import CloseActiveItemButton from '../common/close-active-item-button';
import ConfirmationDialogComponent from '../common/confirmation-dialog.component';
import { prepareExistingFormForSubmission, prepareImportedFormForSubmission } from './form-builder-utils';

interface FormBuilderHeaderProps {
  onClose: (id: string) => void;
  formId?: number;
  jobId?: string;
  moduleFormId?: number;
  templateFormId?: number;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}

const FormBuilderHeader = ({
  onClose,
  formId,
  jobId,
  moduleFormId,
  templateFormId,
  onCloseActiveRecordConfirmationModalRef,
}: FormBuilderHeaderProps) => {
  const resolvedFormId = formId ? String(formId) : crypto.randomUUID(); // resolved id used for change tracking

  const formData = useAppSelector(selectFormData);
  const formCaption = useAppSelector(selectFormCaption);
  const bulkRecordAiAnalysisPrompt = useAppSelector(selectBulkRecordAIAnalysisPrompt);
  const singleRecordAiAnalysisPrompt = useAppSelector(selectSingleRecordAIAnalysisPrompt);
  const isDirty = useAppSelector(selectIsDirty);
  const hasDeletedItems = useAppSelector(selectHasDeletedItems);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { notifyDirty, notifySaveFailed, notifySaveSucceeded } = useDynamicFormNotifier(resolvedFormId);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(formCaption ?? '');

  const { editAccess } = useUserAccess({
    permissions: {
      MODIFY: [FormsPermissions.ADVANCED_FORMBUILDER_CREATE, FormsPermissions.ADVANCED_FORMBUILDER_MODIFY],
    },
  });

  const saveModalReference = useRef<DialogRef>(null);
  const deleteConfirmationModalReference = useRef<DialogRef>(null);
  const destroySubject = useRef(new Subject<void>());

  const [updateFormById] = usePutFormByIdMutation();
  const [updateBulkRecordAIAnalysisPrompt] = useUpdateBulkRecordAiAnalysisPromptMutation();
  const [updateSingleRecordAIAnalysisPrompt] = useUpdateSingleRecordAiAnalysisPromptMutation();
  const [postFormByReferenceId] = usePostFormByReferenceIdMutation();

  // Sync editedName when formCaption changes (e.g., on form load)
  useEffect(() => {
    setEditedName(formCaption ?? '');
  }, [formCaption]);

  useEffect(() => {
    notifyDirty(null, isDirty);
  }, [isDirty, notifyDirty]);

  const close = useCallback(() => {
    if (jobId) {
      onClose(resolvedFormId);
      dispatch(removeBackgroundJob(jobId));
    } else if (formId) {
      onClose(resolvedFormId);
    }
    dispatch(resetState());
  }, [onClose, jobId, formId, resolvedFormId, dispatch]);

  const onFormSave = useCallback(async () => {
    let apiMethod: ReturnType<typeof postFormByReferenceId> | ReturnType<typeof updateFormById> | undefined;

    if (isDirty) {
      if (jobId && formData) {
        const form = prepareImportedFormForSubmission(formData as DynamicFormSettings);
        apiMethod = postFormByReferenceId({ referenceFormId: templateFormId!, form, moduleFormId: moduleFormId! });
      } else if (formData) {
        const form = prepareExistingFormForSubmission(formData as DynamicFormSettings);
        apiMethod = updateFormById(form);
      }
    }

    dispatch(setFormLoadingState(true));
    if (apiMethod) {
      apiMethod
        .unwrap()
        .then(() => {
          notifySaveSucceeded(resolvedFormId);
          showSuccess(t('save-form-success'));
          dispatch(resetState());
        })
        .catch((error) => {
          const errorMessage = extractErrorMessage(error, t('save-form-error'));
          showError(errorMessage);
          notifySaveFailed(resolvedFormId);
        });
    }

    // AI prompt updates
    if ((bulkRecordAiAnalysisPrompt !== undefined || singleRecordAiAnalysisPrompt !== undefined) && formId) {
      try {
        if (bulkRecordAiAnalysisPrompt !== undefined) {
          await updateBulkRecordAIAnalysisPrompt({ formId, body: bulkRecordAiAnalysisPrompt }).unwrap();
        }

        if (singleRecordAiAnalysisPrompt !== undefined) {
          await updateSingleRecordAIAnalysisPrompt({ formId, body: singleRecordAiAnalysisPrompt }).unwrap();
        }

        showSuccess(t('save-ai-analysis-prompt-success'));
        dispatch(resetAiSpecificConfig());
      } catch {
        showError(t('save-ai-analysis-prompt-error'));
      }
    }
    dispatch(setFormLoadingState(false));
  }, [
    formData,
    bulkRecordAiAnalysisPrompt,
    singleRecordAiAnalysisPrompt,
    close,
    jobId,
    templateFormId,
    moduleFormId,
    formId,
    resolvedFormId,
  ]);

  const onSaveFormClick = useCallback(() => {
    if (hasDeletedItems) {
      deleteConfirmationModalReference.current?.show();
    } else {
      onFormSave();
    }
  }, [hasDeletedItems, onFormSave]);

  useEffect(() => {
    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(take(1), takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save') {
          onSaveFormClick();
        } else if (result.buttonName === 'discard') {
          close();
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, [onSaveFormClick, close]);

  const handleClose = () => {
    if (isDirty) {
      saveModalReference.current?.show();
    } else {
      close();
    }
  };

  const handleDeleteConfirmation = () => {
    onFormSave();
    deleteConfirmationModalReference.current?.close();
  };

  const handleDeleteCancel = () => {
    deleteConfirmationModalReference.current?.close();
  };

  const handleEditNameClick = () => {
    setIsEditingName(true);
  };

  const handleNameChange = (value?: string) => {
    setEditedName(value ?? '');
  };

  const getNameError = () => {
    return editedName.trim().length === 0 ? t('required-field', { label: t('name') }) : undefined;
  };

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      applyNameChange();
    } else if (event.key === 'Escape') {
      cancelNameEdit();
    }
  };

  const applyNameChange = () => {
    const trimmedName = editedName.trim();
    if (!trimmedName) {
      return;
    }
    if (trimmedName !== formCaption) {
      dispatch(updateFormCaption(trimmedName));
    } else {
      setEditedName(formCaption ?? '');
    }
    setIsEditingName(false);
  };

  const cancelNameEdit = () => {
    setEditedName(formCaption ?? '');
    setIsEditingName(false);
  };

  return (
    <>
      <div className="bg-white flex items-center justify-between border-b border-gray-4 pb-4 pt-1">
        <div className="group mr-4 flex min-w-0 flex-1 items-center gap-2">
          {isEditingName ? (
            <div className="min-w-0 flex-1" onKeyDown={handleNameKeyDown} onBlur={applyNameChange}>
              <Input
                name="form-name"
                value={editedName}
                onChange={handleNameChange}
                error={getNameError()}
                enableDebounce={false}
                inputRef={(el) => {
                  if (el) {
                    (el as HTMLInputElement).focus();
                  }
                }}
                inputStyles="text-xl font-semibold h-8 w-full"
              />
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-2 overflow-hidden">
              <div className="min-w-0 flex-1">
                <Tooltip description={formCaption ?? 'Form Builder'} tooltipClassName="bg-mono-1" debounceTime={200}>
                  <h1 className="truncate text-xl font-semibold">{formCaption ?? 'Form Builder'}</h1>
                </Tooltip>
              </div>
              {editAccess && (
                <IconButton
                  icon="Pencil"
                  iconSize={OdinIconSize.ExtraSmall}
                  iconClassName="text-gray-3 hover:text-primary-2"
                  onClick={handleEditNameClick}
                  aria-label="Edit form name"
                  tooltip={t('edit')}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex gap-3">
          {editAccess && (
            <Button
              type="primary"
              variant="alternative"
              onClick={onSaveFormClick}
              disabled={!isDirty && !bulkRecordAiAnalysisPrompt && !singleRecordAiAnalysisPrompt}
            >
              {t('save')}
            </Button>
          )}
          <CloseActiveItemButton onClick={handleClose} />
        </div>
      </div>
      <ConfirmationDialogComponent
        ref={saveModalReference}
        onConfirm={onSaveFormClick}
        onClose={close}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
      <ConfirmationDialogComponent
        ref={deleteConfirmationModalReference}
        header={t('this-action-cannot-be-undone')}
        onConfirm={handleDeleteConfirmation}
        onClose={handleDeleteCancel}
        hasCancelButton
      >
        <p>{t('field-group-delete-message')}</p>
      </ConfirmationDialogComponent>
    </>
  );
};

export default React.memo(FormBuilderHeader);
